import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Target, Eye, Heart, Shield, Users, Award, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { publicApi } from '@/lib/api';
import { useSiteContent } from '@/lib/useSiteContent';

const DEFAULT_IMAGES = {
  main: "https://images.pexels.com/photos/9052461/pexels-photo-9052461.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  community: "https://images.unsplash.com/photo-1770892410981-8a6650aa9ee1?crop=entropy&cs=srgb&fm=jpg&w=1200"
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' }
};

const values = [
  { icon: Shield, title: 'Integrity', description: 'We conduct business with honesty and transparency in everything we do.' },
  { icon: Award, title: 'Excellence', description: 'Delivering quality that exceeds expectations in every project we undertake.' },
  { icon: Users, title: 'Community', description: 'Building developments that strengthen and support local communities.' },
  { icon: Lightbulb, title: 'Innovation', description: 'Embracing sustainable practices and forward-thinking design solutions.' },
];

const timeline = [
  { year: 'Foundation', title: 'Company Established', description: 'Koro Living was founded with a vision to transform Manawatu through quality property development.' },
  { year: 'Growth', title: 'First Major Subdivision', description: 'Completed our first large-scale residential subdivision, establishing our reputation for excellence.' },
  { year: 'Expansion', title: 'Commercial Development', description: 'Expanded into commercial property development, supporting local business growth.' },
  { year: 'Today', title: 'Community Leader', description: 'Recognised as a leading property developer in Manawatu with multiple successful projects.' },
];

export default function AboutPage() {
  const [aboutImages, setAboutImages] = useState([]);
  const { tx } = useSiteContent();
  
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await publicApi.getAboutImages();
        setAboutImages(response.data);
      } catch (error) {
        console.error('Error fetching about images:', error);
      }
    };
    fetchImages();
  }, []);
  
  // Get images by index (only "main"/community images, excluding director)
  const getImage = (index) => {
    const mainImages = aboutImages.filter((img) => img.section !== 'director');
    if (mainImages.length > index) {
      return mainImages[index].url;
    }
    return index === 0 ? DEFAULT_IMAGES.main : DEFAULT_IMAGES.community;
  };

  // Get image by section (e.g. director) — falls back to null
  const getImageBySection = (section) => {
    const match = aboutImages.find((img) => img.section === section);
    return match?.url || null;
  };
  
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32" data-testid="about-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="md:col-span-6"
            >
              <p className="text-muted-foreground text-sm tracking-[0.2em] uppercase mb-4">
                {tx('about.hero.eyebrow', 'About Koro Living')}
              </p>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
                {tx('about.hero.heading', "Shaping Manawatu's Future")}
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {tx('about.hero.subtext', 'We are a property and land development company dedicated to creating quality developments that serve the growing needs of Manawatu communities.')}
              </p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="md:col-span-6"
            >
              <div className="image-zoom aspect-[4/3] overflow-hidden">
                <img 
                  src={getImage(0)}
                  alt="Koro Living Development"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Company Overview */}
      <section className="py-20 md:py-32 bg-secondary/50" data-testid="company-overview">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <Eye className="w-5 h-5 text-primary-foreground" />
                </div>
                <h2 className="text-2xl font-semibold">{tx('about.overview.vision_title', 'Our Vision')}</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                {tx('about.overview.vision_body', "To be the leading property developer in Manawatu, recognised for creating sustainable communities that enhance the quality of life for residents while contributing to the region's long-term economic growth.")}
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <Target className="w-5 h-5 text-primary-foreground" />
                </div>
                <h2 className="text-2xl font-semibold">{tx('about.overview.mission_title', 'Our Mission')}</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                {tx('about.overview.mission_body', 'To develop quality homes and land that meet the evolving needs of New Zealand families, while maintaining the highest standards of construction, sustainability, and community consideration in every project we undertake.')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Director Introduction */}
      <section className="py-20 md:py-32" data-testid="director-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="text-muted-foreground text-sm tracking-[0.2em] uppercase mb-4">
              {tx('about.director.eyebrow', 'Leadership')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
              {tx('about.director.heading', 'Meet the Director')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
            {/* Photo + nameplate */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="md:col-span-5"
            >
              <div className="image-zoom aspect-[4/5] overflow-hidden bg-secondary">
                {getImageBySection('director') ? (
                  <img
                    src={getImageBySection('director')}
                    alt="Nick Karan, Managing Director"
                    className="w-full h-full object-cover"
                    data-testid="director-photo"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm uppercase tracking-wider">
                    Director Photo
                  </div>
                )}
              </div>
              <div className="mt-6">
                <h3 className="text-2xl font-semibold tracking-tight" data-testid="director-name">
                  {tx('about.director.name', 'Nick Karan')}
                </h3>
                <p className="text-muted-foreground text-sm uppercase tracking-[0.2em] mt-1">
                  {tx('about.director.title', 'Managing Director')}
                </p>
                <p className="text-muted-foreground text-sm mt-1">
                  {tx('about.director.affiliation', 'Koro Living Limited · Palmerston North, New Zealand')}
                </p>
              </div>
            </motion.div>

            {/* Bio */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="md:col-span-7 space-y-5 text-muted-foreground leading-relaxed"
              data-testid="director-bio"
            >
              <p>{tx('about.director.p1', 'Nick Karan is the Managing Director of Koro Living Limited, a Palmerston North-based property development company specialising in multi-unit residential developments designed to deliver scale, efficiency, and affordability.')}</p>
              <p>{tx('about.director.p2', 'Koro Living is recognised as one of the few developers in the region consistently delivering multi-unit projects at scale, helping address the growing demand for quality, affordable housing in Palmerston North.')}</p>
              <p>{tx('about.director.p3', "Under Nick's leadership, the company has successfully delivered numerous social housing developments in partnership with Kāinga Ora, contributing to the region's housing supply and supporting community growth.")}</p>
              <p>{tx('about.director.p4', "A key focus of Nick's work is making new homes more accessible. Through smart design, efficient construction, and strategic land development, Koro Living consistently delivers some of the most competitively priced new-build homes in the region—without compromising on quality or long-term value.")}</p>
              <p>{tx('about.director.p5', 'Nick remains closely involved in every stage of the development process, from land acquisition and planning through to project delivery. His approach is practical, results-driven, and focused on creating developments that are both commercially successful and socially impactful.')}</p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Our Values */}
      <section className="py-20 md:py-32 bg-secondary/40" data-testid="values-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-muted-foreground text-sm tracking-[0.2em] uppercase mb-4">
              Our Foundation
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
              Core Values
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-border/40 p-8 hover:border-black/20 transition-colors duration-300"
                data-testid={`value-${value.title.toLowerCase()}`}
              >
                <value.icon className="w-10 h-10 text-foreground mb-6" strokeWidth={1.5} />
                <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Industry Experience / Timeline */}
      <section className="py-20 md:py-32 bg-primary text-primary-foreground" data-testid="timeline-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="text-primary-foreground/60 text-sm tracking-[0.2em] uppercase mb-4">
              Our Journey
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
              Building Experience
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {timeline.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="timeline-item relative pl-8 pb-8"
              >
                <div className="absolute left-0 top-1 w-3 h-3 bg-primary-foreground rounded-full" />
                <p className="text-primary-foreground/50 text-sm uppercase tracking-wider mb-2">
                  {item.year}
                </p>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-primary-foreground/70 leading-relaxed text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Community Commitment */}
      <section className="py-20 md:py-32" data-testid="community-commitment">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 md:order-1"
            >
              <div className="image-zoom aspect-[4/3] overflow-hidden">
                <img 
                  src={getImage(1)}
                  alt="Modern Architecture"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 md:order-2"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <Heart className="w-5 h-5 text-primary-foreground" />
                </div>
                <p className="text-muted-foreground text-sm tracking-[0.2em] uppercase">
                  {tx('about.community.eyebrow', 'Our Commitment')}
                </p>
              </div>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
                {tx('about.community.heading', 'Community First')}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {tx('about.community.p1', 'At Koro Living, we believe that successful development goes beyond bricks and mortar. Every project we undertake is designed with the community in mind, ensuring our developments contribute positively to the social fabric of Manawatu.')}
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                {tx('about.community.p2', 'We work closely with local councils, community groups, and residents to understand their needs and aspirations, creating spaces where people can live, work, and thrive for generations to come.')}
              </p>
              <Button asChild className="rounded-full" data-testid="contact-cta">
                <Link to="/contact">
                  Partner With Us
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-secondary/50" data-testid="about-cta">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              See Our Work in Action
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-10">
              Explore our portfolio of completed and ongoing projects across Manawatu.
            </p>
            <Button asChild size="lg" className="rounded-full px-10 py-6" data-testid="view-projects-cta">
              <Link to="/projects">
                View Our Projects
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
